import { ComponentProps, ReactNode, SVGProps } from 'react';
import { Button as AriaButton, Link } from 'react-aria-components';
import { TextButtonVariants, textButton } from './variants';
import { twMerge } from 'tailwind-merge';
import { Typography } from '../typography/typography';
import { match } from 'ts-pattern';

type BaseProps = {
  children: ReactNode;
  className?: string;
  iconLeft?: (props: SVGProps<SVGSVGElement>) => ReactNode;
  iconRight?: (props: SVGProps<SVGSVGElement>) => ReactNode;
} & TextButtonVariants;
type ButtonProps = {
  as?: 'button';
} & Omit<ComponentProps<typeof AriaButton>, 'className' | 'children'>;

type LinkProps = {
  as: 'link';
} & Omit<ComponentProps<typeof Link>, 'className' | 'children'>;

type Props = BaseProps & (ButtonProps | LinkProps);

export function TextButton(props: Props) {
  const {
    as = 'button',
    className,
    children,
    intent,
    size,
    iconLeft: IconLeft,
    iconRight: IconRight,
    ...rest
  } = props;

  const Component = as === 'button' ? AriaButton : Link;

  return (
    <Component
      className={twMerge(textButton({ intent, size }), className)}
      // TODO: Find a way to remove this any
      {...(rest as any)} // eslint-disable-line @typescript-eslint/no-explicit-any
    >
      {IconLeft && <IconLeft className={mapSizeToIconVariant(size)} />}
      <Typography
        as="span"
        variant={mapSizeToTypographyVariant(size)}
        weight={mapSizeToTypographyWeight(size)}
        className="text-currentColor"
      >
        {children}
      </Typography>
      {IconRight && <IconRight className={mapSizeToIconVariant(size)} />}
    </Component>
  );
}

function mapSizeToTypographyVariant(
  size: TextButtonVariants['size'],
): ComponentProps<typeof Typography>['variant'] {
  return match(size)
    .returnType<ComponentProps<typeof Typography>['variant']>()
    .with('tiny', () => 'body-xs')
    .with('small', () => 'body-s')
    .with('default', () => 'body-m')
    .otherwise(() => 'body-m');
}

function mapSizeToTypographyWeight(
  size: TextButtonVariants['size'],
): ComponentProps<typeof Typography>['weight'] {
  return match(size)
    .returnType<ComponentProps<typeof Typography>['weight']>()
    .with('tiny', () => 'medium')
    .with('small', () => 'medium')
    .with('default', () => 'medium')
    .otherwise(() => 'medium');
}

function mapSizeToIconVariant(size: TextButtonVariants['size']): string {
  return match(size)
    .returnType<string>()
    .with('tiny', () => 'min-w-16 w-16 h-16')
    .with('small', () => 'min-w-20 w-20 h-20')
    .with('default', () => 'min-w-24 w-24 h-24')
    .otherwise(() => 'min-w-24 w-24 h-24');
}
